import { Typography, Button, Box, FormLabel, Input, Alert, Card, CardContent } from "@mui/joy";
import { useState, useRef, useCallback } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BlockIcon from "@mui/icons-material/Block";
import { primaryColor } from "../../main/theme/constants";
import { useSiteSettings } from "../../main/context/SiteSettingsContext";
import { sendCustomerOTP, verifyCustomerOTP, switchCustomerAccount } from "../../main/API/customer";
import { MuiPhone } from "../../main/widgets/MuiPhone";
import { setAppLanguage, resolveAppLanguage } from "../../../i18n";
import { translateApiErrorData } from "../../main/utils/masterApiError";
import {
  filterActiveCustomerAccounts,
  isAccountInactiveApiResponse,
} from "../../main/utils/customerAccountApproval";
import { useOtpAutoFill, extractOtpCode } from "../utils/useOtpAutoFill";

function messageFromCustomerApiError(err, t, fallbackKey) {
  const data = err && err.response && err.response.data;
  if (data) {
    const msg = translateApiErrorData(data, t);
    if (msg) return msg;
  }
  return t(fallbackKey);
}

function storeCustomerLoginSession(data) {
  if (!data || !data.access || !data.refresh || !data.user) return false;

  localStorage.setItem("access_token", data.access);
  localStorage.setItem("refresh_token", data.refresh);
  localStorage.setItem("Authorization", `Bearer ${data.access}`);
  localStorage.setItem("user_data", JSON.stringify(data.user));

  if (data.user.roles && Array.isArray(data.user.roles)) {
    const roleNames = data.user.roles.map((role) => role.name);
    localStorage.setItem("user_roles", JSON.stringify(roleNames));
  }

  return true;
}

const WHATSAPP_GREEN = "#25D366";

function WhatsAppCallout({ message, sx = {} }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        padding: "6px 12px",
        borderRadius: "8px",
        backgroundColor: `${WHATSAPP_GREEN}12`,
        border: `1px solid ${WHATSAPP_GREEN}99`,
        marginBottom: 2,
        ...sx,
      }}
    >
      <WhatsAppIcon sx={{ fontSize: 20, color: WHATSAPP_GREEN }} />
      <Typography
        sx={{
          fontWeight: 600,
          color: "#1a1a1a",
          fontSize: "13px",
        }}
      >
        {message}
      </Typography>
    </Box>
  );
}

export default function CustomerLoginScreen() {
  const { t, i18n } = useTranslation("ui");
  const currentLang = resolveAppLanguage(i18n.language);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [step, setStep] = useState("phone"); // 'phone', 'otp', 'select-account', 'pending-approval', 'account-inactive'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [statusDetail, setStatusDetail] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [otpExpirySeconds, setOtpExpirySeconds] = useState(null); // 10 min validity
  const [accounts, setAccounts] = useState([]);
  const { settings } = useSiteSettings();
  const submittedOtpRef = useRef("");

  const OTP_VALIDITY_MINUTES = 10;
  const OTP_VALIDITY_SECONDS = OTP_VALIDITY_MINUTES * 60;

  const resetToPhoneStep = () => {
    setStep("phone");
    setOtpCode("");
    submittedOtpRef.current = "";
    setAccounts([]);
    setError(null);
    setMessage(null);
    setStatusDetail(null);
    setCountdown(0);
    setOtpExpirySeconds(null);
  };

  // Countdown timer for resend OTP
  React.useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // OTP validity countdown (10 minutes)
  React.useEffect(() => {
    if (step !== "otp" || otpExpirySeconds == null || otpExpirySeconds <= 0) return;
    const timer = setInterval(() => {
      setOtpExpirySeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, otpExpirySeconds]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    if (!phone.startsWith("+")) {
      return `+${phone}`;
    }
    return phone;
  };

  const submitOtpVerification = useCallback(async (code) => {
    if (loading) return;

    const formattedPhone = formatPhoneNumber(phoneNumber);

    if (!code || code.length !== 6) {
      setError(t("customerAuth.errOtpDigits"));
      return;
    }

    if (otpExpirySeconds != null && otpExpirySeconds <= 0) {
      setError(t("customerAuth.errOtpExpiredMinutes", { minutes: OTP_VALIDITY_MINUTES }));
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await verifyCustomerOTP(formattedPhone, code);

      if (response && response.status === 200) {
        const data = response.data;

        // Profile completion happens inside the portal (CustomerLayout + AppBar modal).
        // If the backend issued tokens, enter the portal even when still pending approval
        // so an incomplete profile can be finished before the wait-for-approval screen.
        if (storeCustomerLoginSession(data)) {
          localStorage.removeItem("customer_accounts_list");
          setMessage(t("customerAuth.loginRedirecting"));
          setTimeout(() => {
            window.location.href = "/customer-home";
          }, 1000);
          return;
        }

        // No tokens: only show wait-for-approval when the account is pending and
        // the backend did not allow a session (profile cannot be completed here).
        if (data.account_pending_approval === true) {
          // API `message` is English only; the screen renders the translated key.
          setStatusDetail(null);
          setStep("pending-approval");
          return;
        }

        if (data.requires_account_selection && data.accounts) {
          // Keep inactive accounts that still need profile completion selectable.
          // Fully blocked inactive accounts with a complete profile stay out via backend list.
          const selectableAccounts = Array.isArray(data.accounts) ? data.accounts : [];
          const activeAccounts = filterActiveCustomerAccounts(selectableAccounts);
          const accountsForUi =
            activeAccounts.length > 0 ? activeAccounts : selectableAccounts;
          setAccounts(accountsForUi);
          localStorage.setItem("customer_accounts_list", JSON.stringify(accountsForUi));
          setStep("select-account");
          setMessage(t("customerAuth.multipleAccounts"));
          return;
        }

        setError(t("customerAuth.errInvalidOtp"));
        submittedOtpRef.current = "";
      } else {
        const errorData = (response && response.data) || {};
        setError(translateApiErrorData(errorData, t) || t("customerAuth.errInvalidOtp"));
        submittedOtpRef.current = "";
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      const status = err && err.response && err.response.status;
      const errorData = (err && err.response && err.response.data) || {};
      if (isAccountInactiveApiResponse(status, errorData)) {
        setStatusDetail(
          translateApiErrorData(errorData, t) || t("customerAuth.accountInactiveDefaultBody")
        );
        setStep("account-inactive");
        return;
      }
      setError(messageFromCustomerApiError(err, t, "customerAuth.errInvalidOtp"));
      submittedOtpRef.current = "";
    } finally {
      setLoading(false);
    }
  }, [loading, phoneNumber, otpExpirySeconds, t, OTP_VALIDITY_MINUTES]);

  const handleOtpAutoFill = useCallback((code) => {
    const digits = String(code).replace(/\D/g, "").slice(0, 6);
    if (digits.length !== 6) return;
    setOtpCode(digits);
    if (submittedOtpRef.current === digits) return;
    submittedOtpRef.current = digits;
    submitOtpVerification(digits);
  }, [submitOtpVerification]);

  const { tryFillFromClipboard } = useOtpAutoFill({
    enabled: step === "otp" && !loading,
    onOtpReceived: handleOtpAutoFill,
  });

  const handleOtpChange = useCallback((rawValue) => {
    const value = String(rawValue).replace(/\D/g, "").slice(0, 6);
    setOtpCode(value);
    if (value.length !== 6 || loading) return;
    if (submittedOtpRef.current === value) return;
    if (otpExpirySeconds != null && otpExpirySeconds <= 0) return;
    submittedOtpRef.current = value;
    submitOtpVerification(value);
  }, [loading, otpExpirySeconds, submitOtpVerification]);

  // Validate phone number format
  const validatePhoneNumber = (phone) => {
    const formatted = formatPhoneNumber(phone);
    // E.164 format: + followed by 1-15 digits
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(formatted);
  };

  // Handle send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    const formattedPhone = formatPhoneNumber(phoneNumber);

    if (!validatePhoneNumber(formattedPhone)) {
      setError(t("customerAuth.errInvalidPhone"));
      setLoading(false);
      return;
    }

    try {
      const response = await sendCustomerOTP(formattedPhone);

      if (response && response.status === 200) {
        setMessage(t("customerAuth.otpSentSuccess"));
        setStep("otp");
        setCountdown(60); // 60 seconds countdown before resend
        setOtpExpirySeconds(OTP_VALIDITY_SECONDS); // OTP valid for 10 minutes
      } else {
        const errorData = (response && response.data) || {};
        setError(translateApiErrorData(errorData, t) || t("customerAuth.errSendOtpFailed"));
      }
    } catch (err) {
      console.error("Error sending OTP:", err);
      setError(messageFromCustomerApiError(err, t, "customerAuth.errNetwork"));
    } finally {
      setLoading(false);
    }
  };

  // Handle verify OTP (manual button click)
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    submittedOtpRef.current = "";
    await submitOtpVerification(otpCode);
  };

  // Handle switch account (no OTP required)
  const handleSwitchAccount = async (userId) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    const formattedPhone = formatPhoneNumber(phoneNumber);

    try {
      const response = await switchCustomerAccount(userId, formattedPhone);

      if (response && response.status === 200) {
        const data = response.data;

        if (storeCustomerLoginSession(data)) {
          setMessage(t("customerAuth.loginRedirecting"));
          setTimeout(() => {
            window.location.href = "/customer-home";
          }, 1000);
          return;
        }

        setError(t("customerAuth.errSwitchAccountFailed"));
      } else {
        const errorData = (response && response.data) || {};
        setError(translateApiErrorData(errorData, t) || t("customerAuth.errSwitchAccountFailed"));
      }
    } catch (err) {
      console.error("Error switching account:", err);
      const status = err && err.response && err.response.status;
      const errorData = (err && err.response && err.response.data) || {};
      if (isAccountInactiveApiResponse(status, errorData)) {
        setError(
          translateApiErrorData(errorData, t) || t("customerAuth.accountInactiveDefaultBody")
        );
        return;
      }
      setError(messageFromCustomerApiError(err, t, "customerAuth.errSwitchAccountFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (countdown > 0) return;

    setOtpCode("");
    submittedOtpRef.current = "";
    setLoading(true);
    setError(null);
    setMessage(null);

    const formattedPhone = formatPhoneNumber(phoneNumber);

    try {
      const response = await sendCustomerOTP(formattedPhone);

      if (response && response.status === 200) {
        setMessage(t("customerAuth.otpResentSuccess"));
        setCountdown(60); // Reset countdown
        setOtpExpirySeconds(OTP_VALIDITY_SECONDS); // New OTP valid for 10 minutes
      } else {
        const errorData = (response && response.data) || {};
        setError(translateApiErrorData(errorData, t) || t("customerAuth.errResendOtpFailed"));
      }
    } catch (err) {
      console.error("Error resending OTP:", err);
      setError(messageFromCustomerApiError(err, t, "customerAuth.errNetworkShort"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        backgroundColor: "#f7f8fc",
      }}
    >
      {/* Left Side: Login Form */}
      <Box
        sx={{
          width: { xs: "100%", md: 500 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: 4,
          backgroundColor: "#ffffff",
          boxShadow: { xs: "none", md: "0 8px 24px rgba(0, 0, 0, 0.1)" },
          borderRadius: { xs: 0, md: "12px" },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            <Box
              role="group"
              aria-label={t("language.languageToggle")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                p: 0.25,
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb",
              }}
            >
              <Button
                size="sm"
                variant={currentLang === "en" ? "solid" : "plain"}
                color={currentLang === "en" ? "primary" : "neutral"}
                onClick={() => setAppLanguage("en")}
                sx={{ minWidth: 40, fontWeight: 600, fontSize: "12px", py: 0.25, px: 0.75 }}
              >
                {t("language.english")}
              </Button>
              <Button
                size="sm"
                variant={currentLang === "zh" ? "solid" : "plain"}
                color={currentLang === "zh" ? "primary" : "neutral"}
                onClick={() => setAppLanguage("zh")}
                sx={{ minWidth: 40, fontWeight: 600, fontSize: "12px", py: 0.25, px: 0.75 }}
              >
                {t("language.chinese")}
              </Button>
              <Button
                size="sm"
                variant={currentLang === "ar" ? "solid" : "plain"}
                color={currentLang === "ar" ? "primary" : "neutral"}
                onClick={() => setAppLanguage("ar")}
                sx={{ minWidth: 40, fontWeight: 600, fontSize: "12px", py: 0.25, px: 0.75 }}
              >
                {t("language.arabic")}
              </Button>
            </Box>
          </Box>
          <Typography
            level="h2"
            sx={{
              fontWeight: "bold",
              marginBottom: 1,
              color: "#333333",
              textAlign: "center",
            }}
          >
            {t("customerAuth.signInTitle")}
          </Typography>
          <Typography
            level="body2"
            sx={{
              color: "text.secondary",
              marginBottom: 4,
              fontSize: "16px",
              textAlign: "center",
            }}
          >
            {step === "phone"
              ? t("customerAuth.stepPhoneHint")
              : step === "otp"
              ? t("customerAuth.stepOtpHint")
              : step === "select-account"
              ? t("customerAuth.stepSelectHint")
              : step === "pending-approval"
              ? t("customerAuth.pendingRegistrationTitle")
              : step === "account-inactive"
              ? t("customerAuth.accountInactiveTitle")
              : t("customerAuth.stepSelectHint")}
          </Typography>

          {step === "pending-approval" || step === "account-inactive" ? (
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                {step === "pending-approval" ? (
                  <HourglassEmptyIcon sx={{ fontSize: 48, color: primaryColor }} />
                ) : (
                  <BlockIcon sx={{ fontSize: 48, color: "danger.500" }} />
                )}
              </Box>
              <Typography
                level="h3"
                sx={{
                  fontWeight: "bold",
                  marginBottom: 2,
                  color: "#333333",
                }}
              >
                {step === "pending-approval"
                  ? t("customerAuth.pendingRegistrationTitle")
                  : t("customerAuth.accountInactiveTitle")}
              </Typography>
              <Typography
                level="body-md"
                sx={{
                  color: "text.secondary",
                  marginBottom: 3,
                  fontSize: "15px",
                  lineHeight: 1.6,
                }}
              >
                {step === "pending-approval"
                  ? t("customerAuth.pendingApprovalLoginBody")
                  : statusDetail || t("customerAuth.accountInactiveDefaultBody")}
              </Typography>
              <Button
                type="button"
                variant="solid"
                onClick={resetToPhoneStep}
                sx={{
                  width: "100%",
                  padding: "12px 16px",
                  backgroundColor: primaryColor,
                  color: "#ffffff",
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: primaryColor,
                  },
                }}
              >
                {t("customerAuth.backToLogin")}
              </Button>
            </Box>
          ) : step === "phone" ? (
            <form onSubmit={handleSendOTP} style={{ width: "100%" }}>
              <WhatsAppCallout message={t("customerAuth.whatsappSendCode")} />
              <Box sx={{ marginBottom: 3 }}>
                <MuiPhone
                  value={phoneNumber}
                  onChange={(value) => setPhoneNumber(value)}
                  defaultCountry="om"
                  required
                  autoFocus
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f7f9fc",
                      borderRadius: "8px",
                      "&:hover": {
                        borderColor: primaryColor,
                      },
                      "&.Mui-focused": {
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 2px ${primaryColor}33`,
                      },
                    },
                  }}
                />
                <Typography
                  level="body3"
                  sx={{
                    color: "text.secondary",
                    marginTop: "6px",
                    fontSize: "12px",
                  }}
                >
                  {t("customerAuth.phoneHelper")}
                </Typography>
              </Box>

              {error && (
                <Alert
                  color="danger"
                  sx={{
                    marginBottom: 2,
                    borderRadius: "8px",
                  }}
                >
                  {error}
                </Alert>
              )}

              {message && (
                <Alert
                  color="success"
                  sx={{
                    marginBottom: 2,
                    borderRadius: "8px",
                  }}
                >
                  {message}
                </Alert>
              )}

              <Button
                type="submit"
                loading={loading}
                sx={{
                  width: "100%",
                  padding: "12px 16px",
                  backgroundColor: primaryColor,
                  color: "#ffffff",
                  fontWeight: "bold",
                  fontSize: "16px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: primaryColor,
                    transform: "scale(1.02)",
                  },
                }}
              >
                {t("customerAuth.continue")}
              </Button>
            </form>
          ) : step === "otp" ? (
            <form onSubmit={handleVerifyOTP} style={{ width: "100%" }}>
              <WhatsAppCallout message={t("customerAuth.whatsappCodeSent")} />
              <Box sx={{ marginBottom: 3 }}>
                <FormLabel
                  sx={{
                    fontWeight: 600,
                    color: "#555",
                    marginBottom: "8px",
                    fontSize: "14px",
                    display: "block",
                  }}
                >
                  {t("customerAuth.enterOtp")}
                </FormLabel>
                <Input
                  type="tel"
                  value={otpCode}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    const code = extractOtpCode(pasted);
                    if (code) {
                      e.preventDefault();
                      handleOtpChange(code);
                    }
                  }}
                  onFocus={tryFillFromClipboard}
                  placeholder="123456"
                  maxLength={6}
                  required
                  autoFocus
                  slotProps={{
                    input: {
                      autoComplete: "one-time-code",
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    },
                  }}
                  sx={{
                    backgroundColor: "#f7f9fc",
                    borderRadius: "8px",
                    padding: "12px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    border: "1px solid transparent",
                    fontSize: "24px",
                    textAlign: "center",
                    letterSpacing: "8px",
                    fontWeight: "bold",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: primaryColor,
                    },
                    "&:focus, &:active": {
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 2px ${primaryColor}33`,
                    },
                  }}
                />
                <Typography
                  level="body3"
                  sx={{
                    color: "text.secondary",
                    marginTop: "6px",
                    fontSize: "12px",
                    textAlign: "center",
                  }}
                >
                  {t("customerAuth.otpHint", { minutes: OTP_VALIDITY_MINUTES })}
                  {otpExpirySeconds != null && otpExpirySeconds > 0 && (
                    <> {t("customerAuth.otpExpiresIn", {
                      m: String(Math.floor(otpExpirySeconds / 60)),
                      s: String(otpExpirySeconds % 60).padStart(2, "0"),
                    })}</>
                  )}
                  {otpExpirySeconds === 0 && (
                    <Box component="span" sx={{ color: "danger.500", fontWeight: 600 }}>{t("customerAuth.otpExpired")}</Box>
                  )}
                </Typography>
              </Box>

              {error && (
                <Alert
                  color="danger"
                  sx={{
                    marginBottom: 2,
                    borderRadius: "8px",
                  }}
                >
                  {error}
                </Alert>
              )}

              {message && (
                <Alert
                  color="success"
                  sx={{
                    marginBottom: 2,
                    borderRadius: "8px",
                  }}
                >
                  {message}
                </Alert>
              )}

              <Button
                type="submit"
                loading={loading}
                disabled={otpExpirySeconds === 0}
                sx={{
                  width: "100%",
                  padding: "12px 16px",
                  backgroundColor: primaryColor,
                  color: "#ffffff",
                  fontWeight: "bold",
                  fontSize: "16px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease",
                  marginBottom: 2,
                  "&:hover": {
                    backgroundColor: primaryColor,
                    transform: "scale(1.02)",
                  },
                }}
              >
                {t("customerAuth.verifyOtp")}
              </Button>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 2,
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={resetToPhoneStep}
                  sx={{
                    color: primaryColor,
                    borderColor: primaryColor,
                    "&:hover": {
                      borderColor: primaryColor,
                      backgroundColor: `${primaryColor}10`,
                    },
                  }}
                >
                  {t("customerAuth.changeNumber")}
                </Button>

                <Button
                  type="button"
                  variant="plain"
                  onClick={handleResendOTP}
                  disabled={countdown > 0 || loading}
                  sx={{
                    color: primaryColor,
                    "&:hover": {
                      backgroundColor: `${primaryColor}10`,
                    },
                    "&:disabled": {
                      color: "text.secondary",
                    },
                  }}
                >
                  {countdown > 0 ? t("customerAuth.resendIn", { seconds: countdown }) : t("customerAuth.resendOtp")}
                </Button>
              </Box>
            </form>
          ) : (
            <Box sx={{ width: "100%" }}>
              <Typography
                level="body1"
                sx={{
                  color: "text.secondary",
                  marginBottom: 3,
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {t("customerAuth.multipleAccounts")}
              </Typography>

              {error && (
                <Alert
                  color="danger"
                  sx={{
                    marginBottom: 2,
                    borderRadius: "8px",
                  }}
                >
                  {error}
                </Alert>
              )}

              {message && (
                <Alert
                  color="success"
                  sx={{
                    marginBottom: 2,
                    borderRadius: "8px",
                  }}
                >
                  {message}
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  maxHeight: "min(320px, 50vh)",
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: 1,
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {accounts.map((account) => (
                  <Card
                    key={account.id}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: primaryColor,
                        boxShadow: `0 2px 8px ${primaryColor}33`,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography
                            level="title-md"
                            sx={{
                              fontWeight: "bold",
                              marginBottom: 0.5,
                            }}
                          >
                            {account.first_name} {account.last_name}
                          </Typography>
                          {account.first_name_ar && account.last_name_ar && (
                            <Typography
                              level="body2"
                              sx={{
                                color: "text.secondary",
                                marginBottom: 0.5,
                              }}
                            >
                              {account.first_name_ar} {account.last_name_ar}
                            </Typography>
                          )}
                          {account.email && (
                            <Typography
                              level="body3"
                              sx={{
                                color: "text.secondary",
                                marginBottom: 0.5,
                              }}
                            >
                              {account.email}
                            </Typography>
                          )}
                          {account.id_number && (
                            <Typography
                              level="body3"
                              sx={{
                                color: "text.secondary",
                              }}
                            >
                              {t("customerAuth.idPrefix", { id: account.id_number })}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          onClick={() => handleSwitchAccount(account.id)}
                          disabled={loading}
                          sx={{
                            backgroundColor: primaryColor,
                            color: "#ffffff",
                            "&:hover": {
                              backgroundColor: primaryColor,
                            },
                          }}
                        >
                          {loading ? t("actions.loading") : t("actions.select")}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Button
                type="button"
                variant="outlined"
                onClick={resetToPhoneStep}
                sx={{
                  width: "100%",
                  marginTop: 2,
                  color: primaryColor,
                  borderColor: primaryColor,
                  "&:hover": {
                    borderColor: primaryColor,
                    backgroundColor: `${primaryColor}10`,
                  },
                }}
              >
                {t("customerAuth.useDifferentNumber")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Side: Background Image */}
      <Box
        sx={{
          flexGrow: 1,
          backgroundImage:
            settings && settings.welcome_background
              ? `url(${settings.welcome_background})`
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: { xs: "40vh", md: "100%" },
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          },
        }}
      />
    </Box>
  );
}

